import { api } from "@/lib/api";
import type {
	AddMemberInput,
	AddTeamUserInput,
	ClientBilling,
	PartnerBillingSummary,
	PartnerInvoice,
	PartnerMe,
	PartnerMember,
	PartnerMemberRole,
	PartnerOverviewStats,
	PartnerTeamSummary,
	PartnerTeamUser,
	ProvisionTeamInput,
} from "@/types/partner.types";

interface ApiResponse<T> {
	result: T;
}

const unwrap = <T>(res: { data: ApiResponse<T> }): T => res.data.result;

export const partnerApiService = {
	// Overview
	getStats: () =>
		api.get<ApiResponse<PartnerOverviewStats>>("/overview/stats").then(unwrap),

	// Clients (teams)
	listTeams: () =>
		api.get<ApiResponse<PartnerTeamSummary[]>>("/teams").then(unwrap),

	getTeam: (teamId: string) =>
		api.get<ApiResponse<PartnerTeamSummary>>(`/teams/${teamId}`).then(unwrap),

	provisionTeam: (body: ProvisionTeamInput) =>
		api.post<ApiResponse<PartnerTeamSummary>>("/teams", body).then(unwrap),

	listTeamUsers: (teamId: string) =>
		api
			.get<ApiResponse<PartnerTeamUser[]>>(`/teams/${teamId}/users`)
			.then(unwrap),

	addTeamUser: (teamId: string, body: AddTeamUserInput) =>
		api
			.post<ApiResponse<PartnerTeamUser>>(`/teams/${teamId}/users`, body)
			.then(unwrap),

	getTeamBilling: (teamId: string) =>
		api
			.get<ApiResponse<ClientBilling>>(`/teams/${teamId}/billing`)
			.then(unwrap),

	// Current partner + member
	getMe: () => api.get<ApiResponse<PartnerMe>>("/me").then(unwrap),

	// Partner staff
	listMembers: () =>
		api.get<ApiResponse<PartnerMember[]>>("/members").then(unwrap),

	addMember: (body: AddMemberInput) =>
		api.post<ApiResponse<PartnerMember>>("/members", body).then(unwrap),

	updateMemberRole: (memberId: string, body: { role: PartnerMemberRole }) =>
		api
			.patch<ApiResponse<PartnerMember>>(`/members/${memberId}`, body)
			.then(unwrap),

	removeMember: (memberId: string): Promise<void> =>
		api.delete(`/members/${memberId}`).then(() => undefined),

	// Billing
	getBillingSummary: () =>
		api
			.get<ApiResponse<PartnerBillingSummary>>("/billing/summary")
			.then(unwrap),

	listInvoices: () =>
		api.get<ApiResponse<PartnerInvoice[]>>("/billing/invoices").then(unwrap),

	createPortalSession: () =>
		api
			.post<ApiResponse<{ url: string }>>("/billing/portal-session")
			.then(unwrap),
};
